import React, { useState, useRef, useEffect } from "react"

interface ExternalPhotoSrcHeaderProps {
  externalPhotoSrc: string
  alt: string
  onClick?: (e?: React.MouseEvent) => void
  contentDivClassName?: string
  className?: string
  showFullImage?: boolean
}

export const ExternalPhotoSrcHeader: React.FC<ExternalPhotoSrcHeaderProps> = ({
  externalPhotoSrc,
  alt,
  onClick,
  contentDivClassName = "",
  className = "",
  showFullImage = false,
}) => {
  const [imageStyle, setImageStyle] = useState<React.CSSProperties>({ objectFit: "contain" })
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (!showFullImage || !containerRef.current || !imageRef.current) return

    const img = imageRef.current
    const container = containerRef.current

    const updateImageStyle = () => {
      if (!container || !img) return

      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight
      const imgWidth = img.naturalWidth
      const imgHeight = img.naturalHeight

      // If image dimensions are smaller than container, apply responsive sizing
      if (imgWidth < containerWidth && imgHeight < containerHeight) {
        const isLandscape = imgWidth > imgHeight
        setImageStyle({
          objectFit: "contain",
          ...(isLandscape ? { width: "100%" } : { height: "100%" }),
        })
      } else {
        setImageStyle({ objectFit: "contain" })
      }
    }

    // Update when image loads
    if (img.complete) {
      updateImageStyle()
    } else {
      img.addEventListener("load", updateImageStyle)
    }

    // Update on window resize
    window.addEventListener("resize", updateImageStyle)

    return () => {
      img.removeEventListener("load", updateImageStyle)
      window.removeEventListener("resize", updateImageStyle)
    }
  }, [showFullImage, externalPhotoSrc])

  if (showFullImage) {
    return (
      <div
        ref={containerRef}
        className={contentDivClassName}
        style={{
          aspectRatio: "16 / 9",
          backgroundColor: "black",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          ref={imageRef}
          src={externalPhotoSrc}
          alt={alt}
          className={`max-h-full max-w-full ${onClick ? "cursor-pointer" : ""} ${className}`}
          style={imageStyle}
          onClick={onClick as React.MouseEventHandler<HTMLImageElement>}
        />
      </div>
    )
  }

  return (
    <div className={contentDivClassName}>
      <img
        src={externalPhotoSrc}
        alt={alt}
        className={`w-full ${onClick ? "cursor-pointer" : ""} ${className}`}
        style={{ aspectRatio: "16 / 9", objectFit: "cover", objectPosition: "top" }}
        onClick={onClick as React.MouseEventHandler<HTMLImageElement>}
      />
    </div>
  )
}

